import { useMutation } from '@tanstack/react-query'
import { App, Button, Flex, Form } from 'antd'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { apiErrorCode, LoginMethod, track } from 'src/analytics'
import { emailOtpRequest } from 'src/features/auth/api/emailOtp'
import { paths } from 'src/router'
import { useInlineApiErrors } from 'src/utils'

import { AuthLink, EmailInput } from '../components'

type EmailOtpRequestValues = {
  username: string
}

type EmailOtpRequestProps = {
  autoFocus?: boolean
  /** Destination for the "use password instead" link, so it's a real href. */
  passwordTo?: string
  onSwitchToPassword?: () => void
  showPasswordToggle?: boolean
  username?: string
  onUsernameChange?: (value: string) => void
}

export const EmailOtpRequest = ({
  autoFocus = false,
  passwordTo,
  onSwitchToPassword,
  showPasswordToggle,
  username,
  onUsernameChange
}: EmailOtpRequestProps) => {
  const { t } = useTranslation()
  const { message } = App.useApp()
  const navigate = useNavigate()
  const { showApiErrors, errorAlert } = useInlineApiErrors()
  const [form] = Form.useForm<EmailOtpRequestValues>()

  useEffect(() => {
    if ((form.getFieldValue('username') ?? '') === (username ?? '')) {
      return
    }

    form.setFieldValue('username', username)
  }, [form, username])

  const { mutate: requestCode, isPending } = useMutation({
    mutationFn: async (values: EmailOtpRequestValues) => {
      const serviceToken = import.meta.env.VITE_APP_SERVICE_TOKEN as
        | string
        | undefined
      const { data } = await emailOtpRequest({
        username: values.username,
        redirect_url: `${window.location.origin}${paths.auth.emailOtpLink}`,
        ...(serviceToken ? { service_token: serviceToken } : {})
      })

      return { data, submittedEmail: values.username }
    },
    onSuccess: ({ data, submittedEmail }) => {
      // Signing in, not onboarding — see the `login_*` block in the event
      // registry for why these are outside the `onboarding_` namespace.
      track('login_code_requested', {
        method: LoginMethod.EMAIL_OTP,
        resend: false
      })

      if (!data.otp_access_token) {
        // Graceful no-op: we tell the user a code was sent even when the
        // backend refuses (unknown email / feature disabled) to avoid
        // enumeration. They land on the verify screen regardless.
        message.success(t('auth.emailOtp.codeSent'))
      }
      navigate(paths.auth.emailOtpVerify, {
        state: {
          __otpAccessToken: data.otp_access_token,
          __created_at: new Date().toISOString(),
          expires_in: data.expires_in,
          wait: data.wait,
          masked_destination: data.masked_destination,
          submitted_email: submittedEmail
        }
      })
    },
    onError: (error) => {
      track('login_failed', {
        method: LoginMethod.EMAIL_OTP,
        error_code: apiErrorCode(error),
        // The code was never issued, so no verification was attempted.
        attempts: 0
      })
      showApiErrors(error, form)
    }
  })

  const onSubmit = async () => {
    const values = await form.validateFields()
    requestCode(values)
  }

  return (
    <Form
      form={form}
      layout="vertical"
      requiredMark={false}
      noValidate
      id="email-otp-request-form"
      onValuesChange={(changedValues: Partial<EmailOtpRequestValues>) => {
        if (typeof changedValues.username === 'string') {
          onUsernameChange?.(changedValues.username)
        }
      }}
    >
      <Flex vertical gap={16}>
        {errorAlert}

        <EmailInput
          name="username"
          label={t('auth.emailOtp.emailLabel')}
          placeholder={t('auth.emailOtp.emailPlaceholder')}
          autoFocus={autoFocus}
        />

        <Button
          block
          size="large"
          onClick={onSubmit}
          type="primary"
          htmlType="submit"
          loading={isPending}
          form="email-otp-request-form"
        >
          {t('auth.emailOtp.submit')}
        </Button>

        {showPasswordToggle && passwordTo && (
          <Flex justify="center">
            <AuthLink to={passwordTo} onClick={onSwitchToPassword}>
              {t('auth.emailOtp.usePasswordInstead')}
            </AuthLink>
          </Flex>
        )}
      </Flex>
    </Form>
  )
}
